/**
 * Customer Survey Service
 * Handles API calls for customer-facing survey
 * Note: These are public endpoints and do NOT require authentication
 */

import { API_BASE_URL } from '../config/api';
import type {
  GetCustomerSurveyResponse,
  SubmitSurveyRequest,
  SubmitSurveyResponse,
} from '../types/customerSurvey';
import type {
  GenerateReviewRequest,
  GenerateReviewResponse,
  MarkReviewPostedRequest,
  MarkReviewPostedResponse,
  ClaimRewardRequest,
  ClaimRewardResponse,
} from '../types/review';

/**
 * Fetch survey questions for a customer
 * Public endpoint - no authentication required
 */
export const getCustomerSurvey = async (
  campaignId: string,
  uniqueToken: string,
  accessHash: string
): Promise<GetCustomerSurveyResponse> => {
  try {
    const response = await fetch(
      `${API_BASE_URL.PROFILE}/survey/access/${campaignId}/${uniqueToken}/${accessHash}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || 
        errorData.error || 
        `Failed to fetch survey with status ${response.status}`
      );
    }

    const result: GetCustomerSurveyResponse = await response.json();
    console.log('✅ Customer survey loaded:', result);
    return result;
  } catch (error) {
    console.error('❌ Error fetching customer survey:', error);
    throw error;
  }
};

/**
 * Submit customer survey responses
 * Public endpoint - no authentication required
 */
export const submitCustomerSurvey = async (
  campaignId: string,
  uniqueToken: string,
  accessHash: string,
  data: SubmitSurveyRequest
): Promise<SubmitSurveyResponse> => {
  try {
    const response = await fetch(
      `${API_BASE_URL.PROFILE}/survey/submit/${campaignId}/${uniqueToken}/${accessHash}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || 
        errorData.error || 
        `Failed to submit survey with status ${response.status}`
      );
    }

    const result: SubmitSurveyResponse = await response.json();
    console.log('✅ Survey submitted successfully:', result);
    return result;
  } catch (error) {
    console.error('❌ Error submitting survey:', error);
    throw error;
  }
};

/**
 * Generate AI review based on survey responses
 * Public endpoint - no authentication required
 */
export const generateAIReview = async (
  data: GenerateReviewRequest
): Promise<GenerateReviewResponse> => {
  try {
    const response = await fetch(
      `${API_BASE_URL.PROFILE}/reviews/generate`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || 
        errorData.error || 
        `Failed to generate review with status ${response.status}`
      );
    }

    const result: GenerateReviewResponse = await response.json();
    console.log('✅ AI review generated successfully');
    return result;
  } catch (error) {
    console.error('❌ Error generating AI review:', error);
    throw error;
  }
};

/**
 * Mark review as posted by customer (confirm review)
 * Public endpoint - no authentication required
 */
export const markReviewAsPosted = async (
  campaignId: string,
  uniqueToken: string,
  accessHash: string,
  data: MarkReviewPostedRequest
): Promise<MarkReviewPostedResponse> => {
  try {
    // Use CAMPAIGN API base URL (port 8001) as per the API specification
    const response = await fetch(
      `${API_BASE_URL.CAMPAIGN}/survey/confirm-review/${campaignId}/${uniqueToken}/${accessHash}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || 
        errorData.error || 
        `Failed to mark review as posted with status ${response.status}`
      );
    }

    const result: MarkReviewPostedResponse = await response.json();
    console.log('✅ Review marked as posted successfully');
    return result;
  } catch (error) {
    console.error('❌ Error marking review as posted:', error);
    throw error;
  }
};

/**
 * Claim reward after posting review
 * Public endpoint - no authentication required
 */
export const claimReward = async (
  campaignId: string,
  uniqueToken: string,
  accessHash: string,
  data: ClaimRewardRequest
): Promise<ClaimRewardResponse> => {
  try {
    const response = await fetch(
      `${API_BASE_URL.PROFILE}/survey/claim-reward/${campaignId}/${uniqueToken}/${accessHash}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || 
        errorData.error || 
        `Failed to claim reward with status ${response.status}`
      );
    }

    const result: ClaimRewardResponse = await response.json();
    console.log('✅ Reward claimed successfully');
    return result;
  } catch (error) {
    console.error('❌ Error claiming reward:', error);
    throw error;
  }
};

